import { TableCellTypes, Button, ButtonVariants, ButtonSizes } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { makeUnitTypeCell, makeUnitScopedCell, makeDiscrepanciesCell } from '@/components/common';
import { buildRowUnitOptions } from './incomingShipmentUnits';
import { buildDeclarationStatusVariants, buildVerificationStatusVariants } from '@/config/statuses';
import { DECLARATION_STATUS_TONE, VERIFICATION_STATUS_TONE } from '@/config/statusTones';

// Shipment details ▸ Linked declarations tab — columns; reuses the DECLARATIONS_* keys.

const renderUnitType = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));

const ViewDeclarationCell = ({ row, onView }) => {
  const t = useTranslations();
  return (
    <Button variant={ButtonVariants.tertiary} size={ButtonSizes.sm} onClick={() => onView(row)}>
      {t('COMMON_VIEW')}
    </Button>
  );
};

// Clickable, unlike the plain list-page badge — opens this row's discrepancy modal.
export const buildLinkedDeclarationsColumns = (t, { onView, onOpenDiscrepancy }) => [
  {
    id: 'referenceId',
    header: t('DECLARATIONS_COL_REFERENCE_ID'),
    type: TableCellTypes.text,
    width: 140,
    sortable: true,
    pinnable: true,
  },
  {
    id: 'declarationNo',
    header: t('FIELD_DECLARATION_NO'),
    type: TableCellTypes.text,
    width: 140,
    sortable: true,
    pinnable: true,
  },
  {
    id: 'dateOfIssuance',
    header: t('DECLARATIONS_COL_DATE_OF_ISSUANCE'),
    type: TableCellTypes.text,
    width: 150,
    sortable: true,
    pinnable: true,
  },
  {
    id: 'dateOfDispatch',
    header: t('FIELD_DATE_OF_DISPATCH'),
    type: TableCellTypes.text,
    width: 150,
    sortable: true,
    pinnable: true,
  },
  {
    id: 'dateOfDischarge',
    header: t('FIELD_DATE_OF_DISCHARGE'),
    type: TableCellTypes.text,
    width: 150,
    sortable: true,
    pinnable: true,
  },
  {
    id: 'status',
    header: t('FIELD_STATUS'),
    type: TableCellTypes.statusDot,
    width: 160,
    sortable: true,
    pinnable: true,
    variantMap: buildDeclarationStatusVariants(t, DECLARATION_STATUS_TONE),
  },
  {
    id: 'unitType',
    header: t('UNIT_TYPE_ARIA'),
    type: TableCellTypes.custom,
    width: 252,
    sortable: true,
    pinnable: true,
    cell: renderUnitType,
  },
  {
    id: 'assignedQty',
    header: t('FIELD_ASSIGNED_QUANTITY_SHIPMENT'),
    type: TableCellTypes.custom,
    width: 200,
    sortable: true,
    pinnable: true,
    cell: makeUnitScopedCell('assignedQty'),
  },
  {
    id: 'unassignedQty',
    header: t('FIELD_UNASSIGNED_QUANTITY'),
    type: TableCellTypes.custom,
    width: 200,
    sortable: true,
    pinnable: true,
    cell: makeUnitScopedCell('unassignedQty'),
  },
  {
    id: 'receivedQty',
    header: t('FIELD_RECEIVED_DECLARATION_QUANTITY'),
    type: TableCellTypes.custom,
    width: 200,
    sortable: true,
    pinnable: true,
    cell: makeUnitScopedCell('receivedQty'),
  },
  {
    id: 'surplusQty',
    header: t('DECLARATIONS_COL_SURPLUS_QTY'),
    type: TableCellTypes.custom,
    width: 200,
    sortable: true,
    pinnable: true,
    cell: makeUnitScopedCell('surplusQty'),
  },
  {
    id: 'writeOffQty',
    header: t('DECLARATIONS_COL_WRITE_OFF_QTY'),
    type: TableCellTypes.custom,
    width: 200,
    sortable: true,
    pinnable: true,
    cell: makeUnitScopedCell('writeOffQty'),
  },
  {
    id: 'adjustedQty',
    header: t('DECLARATIONS_COL_ADJUSTED_QTY'),
    type: TableCellTypes.custom,
    width: 200,
    sortable: true,
    pinnable: true,
    cell: makeUnitScopedCell('adjustedQty'),
  },
  {
    id: 'availableQty',
    header: t('FIELD_AVAILABLE_QUANTITY'),
    type: TableCellTypes.custom,
    width: 200,
    sortable: true,
    pinnable: true,
    cell: makeUnitScopedCell('availableQty'),
  },
  {
    id: 'ghgEmissions',
    header: t('FIELD_GHG_EMISSIONS'),
    type: TableCellTypes.text,
    width: 200,
    sortable: true,
    pinnable: true,
  },
  {
    id: 'ghgSavings',
    header: t('FIELD_GHG_EMISSION_SAVINGS'),
    type: TableCellTypes.text,
    width: 200,
    sortable: true,
    pinnable: true,
  },
  {
    id: 'rawMaterialType',
    header: t('FIELD_RAW_MATERIAL_TYPE'),
    type: TableCellTypes.text,
    width: 200,
    sortable: true,
    pinnable: true,
  },
  {
    id: 'countryOfOrigin',
    header: t('FIELD_COUNTRY_OF_ORIGIN'),
    type: TableCellTypes.text,
    width: 200,
    sortable: true,
    pinnable: true,
  },
  {
    id: 'feedstockCategory',
    header: t('FIELD_FEEDSTOCK_CATEGORY'),
    type: TableCellTypes.text,
    width: 200,
    sortable: true,
    pinnable: true,
  },
  {
    id: 'allocationPreference',
    header: t('FIELD_ALLOCATION_PREFERENCE'),
    type: TableCellTypes.text,
    width: 200,
    sortable: true,
    pinnable: true,
  },
  {
    id: 'verificationStatus',
    header: t('DECLARATIONS_COL_VERIFICATION'),
    type: TableCellTypes.statusDot,
    width: 150,
    sortable: true,
    pinnable: true,
    variantMap: buildVerificationStatusVariants(t, VERIFICATION_STATUS_TONE),
  },
  {
    id: 'declarations',
    header: t('DECLARATIONS_COL_DECLARATIONS'),
    type: TableCellTypes.custom,
    width: 110,
    sortable: true,
    pinnable: true,
    cell: (ctx) => <ViewDeclarationCell row={ctx.row} onView={onView} />,
  },
  {
    id: 'discrepancies',
    header: t('FIELD_DISCREPANCIES'),
    type: TableCellTypes.custom,
    width: 200,
    sortable: true,
    pinnable: true,
    cell: makeDiscrepanciesCell(onOpenDiscrepancy),
  },
];
